import React from 'react';
import { styled } from '@mui/material';
import { Colors } from 'library';
import { InboxOutlined, ArrowUpward, ArrowDownward, SwapVert } from '@mui/icons-material';
import { REmptyText } from './REmptyText';
import { RTablePagination } from './RTablePagination';

export type RTableSortDirection = 'asc' | 'desc';

export interface RTableColumn<T> {
    key: string;
    label: string;
    sortable?: boolean;
    sortKey?: string;
    render?: (row: T) => React.ReactNode;
    width?: string | number;
}

interface RecruitTableProps<T> {
    columns: RTableColumn<T>[];
    data: T[];
    emptyMessage?: string;
    onRowClick?: (row: T) => void;
    page?: number;
    count?: number;
    perPage?: number;
    onPageChange?: (page: number) => void;
    sortBy?: string;
    RTableSortDirection?: RTableSortDirection;
    onSortChange?: (sortBy: string, direction: RTableSortDirection) => void;
}

export function RTable<T extends { id: string | number }>({
    columns,
    data,
    emptyMessage = 'No results found',
    onRowClick,
    page,
    count,
    perPage,
    onPageChange,
    sortBy,
    RTableSortDirection,
    onSortChange,
}: RecruitTableProps<T>) {
    const hasPagination =
        page !== undefined && count !== undefined && perPage !== undefined && !!onPageChange && count > perPage;

    function handleSort(col: RTableColumn<T>) {
        if (!col.sortable || !onSortChange) return;
        const key = col.sortKey ?? col.key;
        const newDirection: RTableSortDirection = sortBy === key && RTableSortDirection === 'asc' ? 'desc' : 'asc';
        onSortChange(key, newDirection);
    }

    function getSortIcon(col: RTableColumn<T>) {
        const key = col.sortKey ?? col.key;
        if (sortBy === key) {
            return RTableSortDirection === 'asc' ? (
                <ArrowUpward sx={{ fontSize: 14, color: Colors.mediumGrey }} />
            ) : (
                <ArrowDownward sx={{ fontSize: 14, color: Colors.mediumGrey }} />
            );
        }
        return <SwapVert sx={{ fontSize: 14, color: Colors.lightGrey }} />;
    }

    return (
        <TableContainer>
            <StyledTable>
                <thead>
                    <HeaderRow>
                        {columns.map((col) => (
                            <HeaderCell
                                key={col.key}
                                style={{ width: col.width }}
                                clickable={!!col.sortable && !!onSortChange}
                                onClick={() => handleSort(col)}
                            >
                                <HeaderCellContent>
                                    {col.label}
                                    {col.sortable && getSortIcon(col)}
                                </HeaderCellContent>
                            </HeaderCell>
                        ))}
                    </HeaderRow>
                </thead>
                <tbody>
                    {data.length === 0 ? (
                        <tr>
                            <EmptyCell colSpan={columns.length}>
                                <EmptyState>
                                    <InboxOutlined sx={{ fontSize: 40, color: Colors.lightGrey }} />
                                    <REmptyText padding="none">{emptyMessage}</REmptyText>
                                </EmptyState>
                            </EmptyCell>
                        </tr>
                    ) : (
                        data.map((row) => (
                            <DataRow key={row.id} clickable={!!onRowClick}>
                                {columns.map((col, i) => (
                                    <DataCell key={col.key} first={i === 0} interactive={!!col.render && i > 0}>
                                        {i === 0 && onRowClick ? (
                                            <RowButton type="button" onClick={() => onRowClick(row)}>
                                                {col.render
                                                    ? col.render(row)
                                                    : String((row as Record<string, unknown>)[col.key] ?? '')}
                                            </RowButton>
                                        ) : col.render ? (
                                            col.render(row)
                                        ) : (
                                            String((row as Record<string, unknown>)[col.key] ?? '')
                                        )}
                                    </DataCell>
                                ))}
                            </DataRow>
                        ))
                    )}
                </tbody>
            </StyledTable>
            {hasPagination && (
                <PaginationWrapper>
                    <RTablePagination count={count} perPage={perPage} page={page} onPageChange={onPageChange} />
                </PaginationWrapper>
            )}
        </TableContainer>
    );
}

const TableContainer = styled('div')({
    border: `1px solid ${Colors.lightGrey}`,
    borderRadius: 10,
    overflow: 'visible',
    backgroundColor: Colors.white,
});

const StyledTable = styled('table')({
    width: '100%',
    borderCollapse: 'collapse',
});

const HeaderRow = styled('tr')({
    backgroundColor: Colors.fill,
});

const HeaderCell = styled('th')<{ clickable?: boolean }>(({ clickable }) => ({
    padding: '14px 20px',
    textAlign: 'left',
    fontSize: 13,
    fontWeight: 400,
    color: Colors.mediumGrey,
    borderBottom: `1px solid ${Colors.lightGrey}`,
    whiteSpace: 'nowrap',
    cursor: clickable ? 'pointer' : 'default',
    userSelect: clickable ? 'none' : 'auto',
    '&:hover': clickable ? { color: Colors.darkNavy } : {},
    '&:first-child': {
        borderTopLeftRadius: 10,
    },
    '&:last-child': {
        borderTopRightRadius: 10,
    },
}));

const HeaderCellContent = styled('span')({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 2,
});

const DataRow = styled('tr')<{ clickable: boolean }>(({ clickable }) => ({
    position: 'relative',
    borderBottom: `1px solid ${Colors.lightGrey}`,
    '&:hover': {
        backgroundColor: clickable ? Colors.faintGrey : 'transparent',
    },
    '&:last-child': {
        borderBottom: 'none',
    },
}));

const DataCell = styled('td')<{ first?: boolean; interactive?: boolean }>(({ first, interactive }) => ({
    padding: '16px 20px',
    fontSize: 14,
    fontWeight: first ? 500 : 400,
    color: Colors.darkNavy,
    ...(interactive && { position: 'relative', zIndex: 1 }),
    '&:first-child': {
        borderBottomLeftRadius: 10,
    },
    '&:last-child': {
        borderBottomRightRadius: 10,
    },
}));

const RowButton = styled('button')({
    all: 'unset',
    cursor: 'pointer',
    '&::after': {
        content: '""',
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        right: 0,
    },
    outline: 'none',
    '&:focus, &:focus-visible': {
        outline: 'none',
        boxShadow: 'none',
    },
});

const EmptyCell = styled('td')({
    padding: 0,
});

const EmptyState = styled('div')({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '60px 20px',
    gap: 12,
});

const PaginationWrapper = styled('div')({
    borderTop: `1px solid ${Colors.lightGrey}`,
});
